import React, { useEffect, useContext, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { template } from "lodash";

import {
    Button,
    ButtonToolbar,
    Form as RsuiteForm,
    FormInstance,
    Heading,
    Message as RsuiteMessage,
    toaster,
    VStack,
} from "rsuite";
import { TypeAttributes } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import constants from "utils/constants";

import {
    loadDataImportSettings,
    updateDataImportSettings,
} from "api/environments/tenants/data-import-api";
import { getErrorMessage } from "api/defaults";

import { AppDispatch, RootState } from "store/store";
import {
    EntitySetting,
    SettingType,
} from "store/environments/tenants/data-import/data-import-types";

import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";

import {
    getIsModalLoaderVisible,
    setModalLoaderVisibility,
} from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";

import ModalContainer from "components/Common/Modal/ModalContainer";

import { HistoryContext } from "../Table";

import InputNumber from "./Form/InputNumber/InputNumber";
import Input from "./Form/Input/Input";

import "./Settings.less";
import {
    SettingsFormValue,
    toModel,
    toEntitySettings,
    toFormValue,
} from "./settings-helpers";
import { descriptions } from "./definitions";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    open: boolean;

    setOpen?: (open: boolean) => void;
}

const Settings: React.FC<Props> = ({ open, setOpen }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const cancelTokenSource = axios.CancelToken.source();

    const formRef = useRef<FormInstance<SettingsFormValue> | null>(null);

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const isLoading = useSelector((state: RootState): boolean =>
        getIsModalLoaderVisible(state)
    );

    const dataImport = useContext(HistoryContext);
    const entityName = dataImport?.entity;

    const [settings, setSettings] = useImmer<Record<
        string,
        EntitySetting
    > | null>(null);

    const [formValue, setFormValue] = useImmer<SettingsFormValue>({});

    const [updatePermitted, setUpdatePermitted] = useState<boolean>(true);

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const minHeight = constants.dimensions.table.minModalHeight;

    const title = useMemo(
        () =>
            template(t("tenantConfig.axImportControl.settings.modal.title"))({
                entityName,
            }),
        [t, entityName]
    );

    const successMessage = useMemo(
        () =>
            template(
                t("tenantConfig.axImportControl.settings.modal.successMessage")
            )({
                entityName,
            }),
        [t, entityName]
    );

    const model = useMemo(() => toModel(settings), [settings]);

    const groups = useMemo(
        () =>
            Object.entries(settings ?? {}).reduce<React.ReactElement[]>(
                (acc, [key, setting]) => {
                    const group = descriptions.get(key);

                    if (!group) {
                        return acc;
                    }

                    switch (setting.type) {
                        case SettingType.Number: {
                            acc.push(
                                <InputNumber
                                    key={key}
                                    name={key}
                                    value={formValue[key] ?? ""}
                                    title={group.title}
                                    helpText={group.description}
                                />
                            );

                            break;
                        }
                        case SettingType.String: {
                            acc.push(
                                <Input
                                    key={key}
                                    name={key}
                                    value={String(formValue[key] ?? "")}
                                    title={group.title}
                                    helpText={group.description}
                                />
                            );

                            break;
                        }
                        default:
                            break;
                    }

                    return acc;
                },
                []
            ),
        [settings, formValue]
    );

    const handleModalClose = () => {
        setOpen?.(false);
        cancelTokenSource?.cancel();
    };

    const changeDataImportSettings = async () => {
        if (!settings) {
            return;
        }

        try {
            const [payload, status] = await updateDataImportSettings(
                activeEnvironmentId,
                activeTenantId,
                dataImport!.entity,
                settings
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        dispatch(setModalLoaderVisibility(true));

        const promises = [changeDataImportSettings()];

        Promise.all(promises).then((value) => {
            dispatch(setModalLoaderVisibility(false));

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = successMessage;
            }

            toaster.push(
                <RsuiteMessage showIcon type={type}>
                    {message}
                </RsuiteMessage>,
                toastOptions
            );

            handleModalClose();
        });
    };

    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessageForModal(""));

        Promise.all([fetchDataImportSettings()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessageForModal(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
            });
    };

    const fetchDataImportSettings = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadDataImportSettings(
                activeEnvironmentId,
                activeTenantId,
                dataImport!.entity,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setSettings(payload);
                setFormValue(toFormValue(payload));

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleFormChange = (value: SettingsFormValue) => {
        setFormValue(value);
        setSettings(toEntitySettings(value, settings));
    };

    useEffect(() => {
        if (open && activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [open, activeEnvironmentId, activeTenantId]);

    useEffect(() => {
        const validated = formRef.current?.check() ?? true;

        const permitted = validated && !isLoading;

        setUpdatePermitted(permitted);
    }, [formValue, isLoading]);

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                appearance="primary"
                onClick={handleSubmit}
                disabled={!updatePermitted}
            >
                {t("tenantConfig.axImportControl.settings.modal.submitBtn")}
            </Button>

            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("tenantConfig.axImportControl.settings.modal.cancelBtn")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className={"data-import-settings__modal"}
            open={open}
            header={header}
            footer={footer}
            onClose={handleModalClose}
        >
            <RsuiteForm
                style={{ marginBottom: "0.5rem", minHeight: minHeight }}
                ref={formRef}
                model={model}
                formValue={formValue}
                onChange={handleFormChange}
                fluid
            >
                <VStack spacing={20} justifyContent={"flex-start"}>
                    {groups.map((field) => (
                        <VStack.Item key={field.key} alignSelf={"flex-start"}>
                            {field}
                        </VStack.Item>
                    ))}
                </VStack>
            </RsuiteForm>
        </ModalContainer>
    );
};

export default Settings;
